import { useCallback } from 'react'
import { useHistory } from '../../../../../node_modules/react-router-dom/cjs/react-router-dom.min'
import { useApiListRolePermission } from '../../../../api-new/state/content'
import { decodeJwtClaims } from '../../../../shared/jwt'
import { getAccessToken } from '../../../../storage/auth'
import { hasAdminNavigation, isAdminSection, orgRolesOf } from '../../../../role/hook/admin-navigation'
import { SCHEDULED_PATH, isRestrictedSession } from '../../../../shared/organization-deletion/organization-deletion'

export const useBuildRouteRedirect = () => {
  return useCallback(path => {
    let url = new URL(path, process.env.REACT_APP_SITE)
    url.searchParams.set('redirect', btoa(`${document.location.pathname}${document.location.search}`))
    return url.toString()
  }, [])
}

export const useBuildRouteRedirectDoc = () => {
  return useCallback(path => {
    let url = new URL(path, process.env.REACT_APP_SITE)
    url.searchParams.set('redirect-doc', btoa(`${document.location.pathname}${document.location.search}`))
    return url.toString()
  }, [])
}

export const useBuildRouteArgs = () => {
  return useCallback((path, redirect, redirectDoc, email) => {
    let url = new URL(path, process.env.REACT_APP_SITE)

    if (redirect) {
      url.searchParams.set('redirect', redirect)
    }

    if (redirectDoc) {
      url.searchParams.set('redirect-doc', redirectDoc)
    }

    if (email) {
      url.searchParams.set('email', email)
    }

    return { path: `${url.pathname}${url.search}`, url: url.toString() }
  }, [])
}

// Where a login lands when nothing more specific was asked for.
//
// The dashboard needs the org DASHBOARD scope, so that scope -- not a list of
// role names -- decides who is sent there. The old check looked for
// sys-admin/admin/author/owner/teacher, and neither `teacher` nor `owner`
// implies DASHBOARD: a teacher at a customer org and the owner of a
// self-registered personal org both landed on a page they could not read,
// where the widgets then issued requests the API denied. Self-registration
// makes every new user an owner, so that was most of them.
//
// Anyone without it goes to the downloads page, which is the useful half of
// the panel for an unprivileged user.
const useLandingRoute = () => {
  const apiListRolePermission = useApiListRolePermission()

  // landing: where to go; adminNav: whether this person has any admin
  // navigation in the organization (the sidebar's sections).
  return useCallback(async () => {
    const claims = decodeJwtClaims(getAccessToken()) || {}

    // Matches the backend: RBAC grants any ORG scope to sys admin and sys root
    // before consulting role_org_permissions.
    if (claims.isSysAdmin || claims.isSysRoot) return { landing: '/org/dashboard', adminNav: true }

    if (!claims.userID) return { landing: '/download/links', adminNav: false }

    try {
      const permissions = await apiListRolePermission.get(`${claims.userID}/user`)

      const canReadDashboard = (permissions || []).some(
        permission => permission.identifier === 'DASHBOARD' && permission.accessRead
      )

      return {
        landing: canReadDashboard ? '/org/dashboard' : '/download/links',
        adminNav: hasAdminNavigation({ claims, orgRoles: orgRolesOf(permissions) })
      }
    } catch {
      // Downloads is the safe fallback: it needs no scope, so an unreadable
      // permission list cannot strand anyone on a page that will only 403.
      return { landing: '/download/links', adminNav: false }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

// The sign-in has to ask which organization: the auth service minted the
// session in a fallback organization but left lastOrganizationID null
// because the person belongs to several company organizations and has not
// chosen one (rapid-api-auth common/auth landing.go). The same two claims are
// how the Unity viewers decide to show their organization picker. A PIN
// session cannot switch, so it is never asked.
//
// A restricted restore session (orgPendingDeletion) is never asked: it may
// still carry multipleAccounts with no lastOrganizationID, but it can only
// use the restore page (delete organization, plan Task 27, N3).
export const needsOrganizationChoice = claims =>
  !!claims && !isRestrictedSession(claims) && !!claims.multipleAccounts && claims.lastOrganizationID == null && !claims.pinAuthenticated

export const useLoginRedirect = () => {
  const history = useHistory()

  const landingRoute = useLandingRoute()

  // skipChoice: the organization was just chosen (/auth/account); go on
  // whatever the new token says.
  return useCallback(async ({ skipChoice = false } = {}) => {
    const searchParams = new URLSearchParams(document.location.search)

    const redirect = searchParams.get('redirect')
    const redirectDoc = searchParams.get('redirect-doc')

    const claims = decodeJwtClaims(getAccessToken())

    // The owner's restricted restore session goes to the restore page
    // whatever was asked for: it is the only page that session can use.
    if (isRestrictedSession(claims)) {
      history.replace(SCHEDULED_PATH)
      return
    }

    // Ask first; the chooser page calls this again once the switch has set
    // lastOrganizationID, carrying the same destination.
    if (!skipChoice && needsOrganizationChoice(claims)) {
      const choose = new URLSearchParams()
      if (redirect) choose.set('redirect', redirect)
      if (redirectDoc) choose.set('redirect-doc', redirectDoc)
      const query = choose.toString()
      history.replace(`/auth/account${query ? `?${query}` : ''}`)
      return
    }

    // An explicit destination wins for EVERY user. This is deliberately ahead
    // of the privilege check: a learner who follows a course or doc link and
    // is asked to sign in must arrive where they were going, not at a default
    // landing page. Only the fallback depends on what they can read.
    const { landing, adminNav } = await landingRoute()

    if (redirect) {
      // Guard the decode: a malformed or legacy raw redirect must not throw and
      // strand the user on the login screen.
      let target = null
      try {
        target = atob(redirect)
      } catch {
        target = null
      }

      // A destination in an admin section is only honoured for someone with
      // admin navigation. Signing out from an admin page carries that page as
      // the destination (logout-destination.js); if a learner signs in next
      // on the same browser, they land where they belong instead of on an
      // empty admin page (owner retest 2, 2026-09-28).
      //
      // The restore page is a destination only for a restricted session
      // (handled above): an owner signed out to it whose organization was
      // restored meanwhile lands as usual.
      const usable = target && target.split(/[?#]/)[0] !== SCHEDULED_PATH && !(isAdminSection(target) && !adminNav)
      history.replace(usable ? target : landing)
      return
    }

    if (redirectDoc) {
      try {
        window.location.replace(atob(redirectDoc))
      } catch {
        history.replace(landing)
      }
      return
    }

    history.replace(landing)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
